import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { collection, doc, getDoc, addDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";

interface FormField {
  id: string;
  type: "text" | "textarea" | "select" | "radio" | "checkbox";
  label: string;
  required: boolean;
  options?: string[];
}

interface Form {
  id: string;
  name: string;
  description: string;
  fields: FormField[];
  createdAt: Date;
  updatedAt: Date;
}

interface FormResponse {
  formId: string;
  responses: {
    [fieldId: string]: string | string[];
  };
  submittedAt: Date;
}

export default function PublicFormPage() {
  const { formId } = useParams<{ formId: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<Form | null>(null);
  const [loading, setLoading] = useState(true);
  const [responses, setResponses] = useState<{ [fieldId: string]: string | string[] }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchForm = async () => {
      if (!formId) return;

      try {
        const formDoc = await getDoc(doc(db, "forms", formId));
        
        if (!formDoc.exists()) {
          toast({
            variant: "destructive",
            title: "Formular nicht gefunden",
            description: "Das angeforderte Formular existiert nicht mehr.",
          });
          navigate("/");
          return;
        }

        const formData = formDoc.data() as Form;
        setForm({
          ...formData,
          createdAt: formData.createdAt.toDate(),
          updatedAt: formData.updatedAt.toDate(),
        });

        // Initialize responses object
        const initialResponses: { [fieldId: string]: string | string[] } = {};
        formData.fields.forEach(field => {
          if (field.type === "checkbox") {
            initialResponses[field.id] = [];
          } else {
            initialResponses[field.id] = "";
          }
        });
        setResponses(initialResponses);

      } catch (error) {
        console.error("Error fetching form:", error);
        toast({
          variant: "destructive",
          title: "Fehler",
          description: "Das Formular konnte nicht geladen werden.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [formId, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;

    console.log("Form submission started", { formId: form.id, responses });

    // Validate required fields
    const missingFields = form.fields
      .filter(field => field.required)
      .filter(field => {
        const response = responses[field.id];
        if (Array.isArray(response)) {
          return response.length === 0;
        }
        return !response;
      });

    if (missingFields.length > 0) {
      console.log("Missing required fields:", missingFields);
      toast({
        variant: "destructive",
        title: "Pflichtfelder fehlen",
        description: `Bitte füllen Sie alle Pflichtfelder aus: ${missingFields.map(f => f.label).join(", ")}`,
      });
      return;
    }

    setSubmitting(true);

    try {
      const formResponse: FormResponse = {
        formId: form.id,
        responses,
        submittedAt: new Date(),
      };

      console.log("Attempting to save form response:", formResponse);

      const docRef = await addDoc(collection(db, "formResponses"), {
        ...formResponse,
        submittedAt: Timestamp.fromDate(formResponse.submittedAt),
      });

      console.log("Form response saved successfully with ID:", docRef.id);

      toast({
        title: "Vielen Dank!",
        description: "Ihre Antwort wurde erfolgreich übermittelt.",
      });

      // Reset form
      const initialResponses: { [fieldId: string]: string | string[] } = {};
      form.fields.forEach(field => {
        if (field.type === "checkbox") {
          initialResponses[field.id] = [];
        } else {
          initialResponses[field.id] = "";
        }
      });
      setResponses(initialResponses);

    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Ihre Antwort konnte nicht übermittelt werden. Bitte versuchen Sie es später erneut.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (fieldId: string, value: string | string[]) => {
    setResponses(prev => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Formular wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!form) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container max-w-2xl mx-auto px-4">
        <Card>
          <CardHeader>
            <CardTitle>{form.name}</CardTitle>
            {form.description && (
              <CardDescription>{form.description}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {form.fields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <label className="text-sm font-medium">
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </label>

                  {field.type === "text" && (
                    <Input
                      type="text"
                      value={responses[field.id] as string}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      required={field.required}
                    />
                  )}

                  {field.type === "textarea" && (
                    <Textarea
                      value={responses[field.id] as string}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      required={field.required}
                    />
                  )}

                  {field.type === "select" && (
                    <select
                      className="w-full rounded-md border border-input bg-background px-3 py-2"
                      value={responses[field.id] as string}
                      onChange={(e) => handleInputChange(field.id, e.target.value)}
                      required={field.required}
                    >
                      <option value="">Bitte wählen...</option>
                      {field.options?.map((option, index) => (
                        <option key={index} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}

                  {field.type === "radio" && (
                    <div className="space-y-2">
                      {field.options?.map((option, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id={`${field.id}-${index}`}
                            name={field.id}
                            value={option}
                            checked={(responses[field.id] as string) === option}
                            onChange={(e) => handleInputChange(field.id, e.target.value)}
                            required={field.required}
                            className="h-4 w-4 border-gray-300"
                          />
                          <label htmlFor={`${field.id}-${index}`} className="text-sm">
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}

                  {field.type === "checkbox" && (
                    <div className="space-y-2">
                      {field.options?.map((option, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`${field.id}-${index}`}
                            value={option}
                            checked={(responses[field.id] as string[]).includes(option)}
                            onChange={(e) => {
                              const currentValues = responses[field.id] as string[];
                              const newValues = e.target.checked
                                ? [...currentValues, option]
                                : currentValues.filter((v) => v !== option);
                              handleInputChange(field.id, newValues);
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <label htmlFor={`${field.id}-${index}`} className="text-sm">
                            {option}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? "Wird gesendet..." : "Absenden"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 
